/**
 * Embed widget telemetry — small, batched event emitter that POSTs to
 * /events/v1/ingest (the generic client telemetry endpoint shared by
 * embed/webapp/RN/portal).
 *
 * Design:
 *   - One module-level queue. emit() pushes; a 5s timer flushes.
 *   - On `pagehide`/`visibilitychange → hidden` we flush via fetch with
 *     `keepalive: true` so the request completes after the iframe unloads
 *     while still carrying the JWT Authorization header (sendBeacon can't).
 *   - All transport is best-effort: failures swallow silently — telemetry
 *     must never crash the widget.
 *   - Auth: uses the embed's authed httpClient, which mints a guest JWT
 *     via @wg/auth-core. The instance public_token is no longer the auth
 *     token; it's just an identifier carried in source_context.
 *   - visitor_id is per-instance, persisted in localStorage. Lets us count
 *     uniques without tracking across partners.
 */

import { httpClient } from '../client';

const FLUSH_INTERVAL_MS = 5000;
const PRECONFIG_BUFFER_MAX = 200;
const VISITOR_KEY_PREFIX = 'wg_embed_vid:';

type ClientEvent = {
  type: string;
  payload?: Record<string, unknown>;
  timestamp?: number;
};

type SourceContext = {
  instance_id: string;
  partner_id?: string;
  parent_origin?: string | null;
  visitor_id?: string | null;
};

let sourceContext: SourceContext | null = null;
let visitorId: string | null = null;
let queue: ClientEvent[] = [];
let queueSessionIds: (string | null)[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let configured = false;

function safeUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return (crypto as { randomUUID: () => string }).randomUUID();
    }
  } catch {
    /* fallthrough */
  }
  return `vid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function loadVisitor(instanceKey: string): string {
  const key = VISITOR_KEY_PREFIX + instanceKey;
  try {
    const existing = window.localStorage.getItem(key);
    if (existing && existing.length < 200) return existing;
  } catch {
    /* private mode, etc. */
  }
  const fresh = safeUuid();
  try {
    window.localStorage.setItem(key, fresh);
  } catch {
    /* ignore */
  }
  return fresh;
}

export function configureTelemetry(opts: {
  /** Resolved instance UUID — denormalized into source_context.instance_id. */
  instanceId: string;
  /** Resolved partner UUID — denormalized for partner-portal analytics. */
  partnerId?: string;
  /** Origin of the parent page hosting the iframe. */
  parentOrigin: string | null;
  /**
   * Stable per-instance key used to scope the visitor id in localStorage.
   * Usually the same as instanceId; kept as a separate arg in case a partner
   * wants to share visitor identity across multiple instances they own.
   */
  instanceKey: string;
}): void {
  visitorId = loadVisitor(opts.instanceKey);
  sourceContext = {
    instance_id: opts.instanceId,
    partner_id: opts.partnerId,
    parent_origin: opts.parentOrigin,
    visitor_id: visitorId,
  };
  configured = true;
  startTimer();
  attachLifecycleHandlers();
}

/** Returns the per-instance visitor id once telemetry is configured. */
export function getVisitorId(): string | null {
  return visitorId;
}

function startTimer(): void {
  if (timer) return;
  timer = setInterval(() => {
    flush().catch(() => undefined);
  }, FLUSH_INTERVAL_MS);
}

let lifecycleAttached = false;
function attachLifecycleHandlers(): void {
  if (lifecycleAttached) return;
  lifecycleAttached = true;

  // We can't use sendBeacon: the events ingest requires an Authorization
  // header (JWT) and beacon doesn't support custom headers. fetch with
  // keepalive:true is the modern replacement — the request continues after
  // the iframe unloads and carries headers normally.
  const flushOnHide = () => {
    flush().catch(() => undefined);
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnHide();
  });
  window.addEventListener('pagehide', flushOnHide);
}

function buildBatch(events: ClientEvent[], sessionIds: (string | null)[]) {
  // The current ingest contract has a single tracking_session_id per batch.
  // Pick the most recent non-null session_id from this batch to associate;
  // if events span multiple sessions (rare — each session triggers its own
  // batch), the others lose the linkage but still ingest with their props.
  const trackingSessionId =
    [...sessionIds].reverse().find((s): s is string => !!s) ?? null;
  return {
    source: 'embed' as const,
    source_id: sourceContext?.instance_id ?? null,
    tracking_session_id: trackingSessionId,
    source_context: sourceContext ?? {},
    events,
  };
}

async function flush(): Promise<void> {
  if (!configured || queue.length === 0 || !sourceContext) return;
  const batch = queue.splice(0, queue.length);
  const sessionIds = queueSessionIds.splice(0, queueSessionIds.length);
  const body = buildBatch(batch, sessionIds);
  try {
    await httpClient.post('/events/v1/ingest', body);
  } catch {
    /* drop on failure — telemetry is best-effort */
  }
}

/**
 * Push an event to the queue. Safe to call before configureTelemetry — the
 * event is buffered (up to PRECONFIG_BUFFER_MAX) and replayed once configured.
 */
export function emit(
  eventType: string,
  args?: { sessionId?: string | null; props?: Record<string, unknown> },
): void {
  const ev: ClientEvent = {
    type: eventType,
    payload: args?.props ?? {},
    timestamp: Date.now(),
  };
  if (queue.length >= PRECONFIG_BUFFER_MAX) {
    // Drop oldest first.
    queue.shift();
    queueSessionIds.shift();
  }
  queue.push(ev);
  queueSessionIds.push(args?.sessionId ?? null);
}

/** Manually force a flush — handy on logout / nav-away. */
export function flushNow(): void {
  flush().catch(() => undefined);
}
